import { useMemo, useCallback, useEffect, useRef } from 'react';
import { IWorkerData } from '@th-types/worker.type';
import { IChatMember } from '@th-types/person.type';
import { BASE_PATH } from '@utils/Environment';
import { useInfiniteQuery } from '@tanstack/react-query';
import IDirectMessageChat from '@th-types/direct-messages-chat.type';
import { AxiosError } from 'axios';
import QueryKeys from '@constants/queryKeys';
import { useUserContext } from '@state/userContext';
import {
  fetchDirectMessages,
  fetchWorkerDirectMessages,
  sendWorkerDirectMessage,
} from '@company/services/directMessage/api';
import { useMessageMutation } from '@hooks/useMessageMutation';
import { useDirectMessageContext } from '@company/state/directMessageContext';
import { useCompanyContext } from '@company/state/companyContext';
import { isAnnouncementChat } from '@constants/directChat';
import {
  DIRECT_CHAT_MESSAGE_PAGE_SIZE,
  hasMoreOlderMessages,
  nextOlderMessagesOffset,
} from '@utils/directChatPagination';
import GenericChat from './GenericChat';
import './styles.css';

const PREMIUM_DM_POLICY_NOTICE =
  'Direct messaging is ONLY for specific job opportunities, not general database building. Companies found in violation could lose this functionality.';

interface DirectMessageChatProps {
  showWorkerInfo?: boolean;
  worker: IWorkerData;
  chatId?: number;
  onClose: () => void;
}

function emptyChat(
  worker: IWorkerData,
  chatId: number = 0
): IDirectMessageChat {
  return {
    id: chatId,
    receiver: worker.id,
    version: 0,
    lastUpdated: new Date(),
    dateCreate: new Date(),
    worker,
    messages: [],
  };
}

function DirectMessageChat({
  showWorkerInfo,
  worker,
  chatId,
  onClose,
}: DirectMessageChatProps) {
  const { id: userId } = useUserContext();
  const { markChatAsRead, originalDirectMessage } = useDirectMessageContext();
  const { premium: isPremiumCompany } = useCompanyContext();
  const markedAsReadRef = useRef<number | null>(null);

  const policyNotice = useMemo(
    () =>
      isPremiumCompany ? (
        <div className="dm-policy-notice">
          <small className="fst-italic">{PREMIUM_DM_POLICY_NOTICE}</small>
        </div>
      ) : undefined,
    [isPremiumCompany]
  );

  const queryKey = chatId
    ? `${QueryKeys.DIRECT_MESSAGE_CONVERSATIONS}_${chatId}`
    : `${QueryKeys.DIRECT_MESSAGE_CONVERSATIONS}_${worker.id}`;

  const {
    data,
    isLoading: isLoadingMessages,
    error: messagesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<IDirectMessageChat, AxiosError>({
    queryKey: [queryKey],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number;
      const page = { max: DIRECT_CHAT_MESSAGE_PAGE_SIZE, offset };
      try {
        if (chatId) {
          return await fetchDirectMessages(chatId, page);
        }
        return await fetchWorkerDirectMessages(worker.id, page);
      } catch (error) {
        if (offset === 0 && (error as AxiosError)?.response?.status === 404) {
          /**
           * If there is no direct message chat for the worker the BE returns a 404 error
           * In this case we return an empty chat structure and don't retry the query
           */
          return emptyChat(worker, chatId);
        }
        throw error;
      }
    },
    getNextPageParam: (lastPage) => {
      const pageLen = (lastPage.messages || []).length;
      if (!hasMoreOlderMessages(lastPage.pagination, pageLen)) {
        return undefined;
      }
      return nextOlderMessagesOffset(lastPage.pagination) ?? undefined;
    },
    retry: false,
  });

  const chatMeta = data?.pages?.[0];

  // Only mark once per chat to avoid duplicate API calls
  // Only call if there are actually unread messages to avoid unnecessary API calls
  useEffect(() => {
    if (chatMeta?.id && markedAsReadRef.current !== chatMeta.id) {
      markedAsReadRef.current = chatMeta.id;
      // Only mark as read if there are unread messages
      const hasUnreadMessages =
        originalDirectMessage?.unreadMessages &&
        originalDirectMessage.unreadMessages > 0;
      if (hasUnreadMessages) {
        markChatAsRead(chatMeta.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMeta?.id]);

  const allDirectMessages = useMemo(() => {
    if (!data?.pages?.length) return [];
    // pages[0] is newest; reverse so older pages come first, then ascending within each page
    const flattened = data.pages
      .slice()
      .reverse()
      .flatMap((page) => page.messages || []);
    // Offsets count from the newest message, so messages sent after a page was
    // loaded shift later pages and can repeat rows we already have.
    const seenIds = new Set<number>();
    return flattened.filter((message) => {
      if (seenIds.has(message.id)) return false;
      seenIds.add(message.id);
      return true;
    });
  }, [data]);

  const chatMembers: IChatMember[] = useMemo(() => {
    if (!chatMeta) return [];
    const membersMap = new Map<number, IChatMember>();
    if (chatMeta.worker) {
      membersMap.set(chatMeta.worker.id, chatMeta.worker);
    }
    if (chatMeta.me) {
      membersMap.set(chatMeta.me.id, chatMeta.me);
    }
    allDirectMessages.forEach((msg) => {
      if (msg.author) {
        membersMap.set(msg.author.id, msg.author);
      }
    });
    return Array.from(membersMap.values());
  }, [chatMeta, allDirectMessages]);

  /**
   * Transform the messages data to the format used by the GenericChat component
   * while still still compatible with the JobMessageChat component
   */
  const transformedMessages = useMemo(() => {
    return allDirectMessages.map((msg) => ({
      ...msg,
      type: 'message' as const,
      authorId: msg.author.id,
      created: msg.dateCreated,
      jobId: 0,
      readBy: [],
    }));
  }, [allDirectMessages]);

  const isAnnouncement = isAnnouncementChat({
    type: chatMeta?.type ?? originalDirectMessage?.type,
  });

  const viewProfile = useCallback(() => {
    window.open(
      `${BASE_PATH}workers/${worker.customUrl ?? worker.id}`,
      '_blank'
    );
  }, [worker]);

  const { mutateAsync: sendMessage, isPending: isSendingMessage } =
    useMessageMutation({
      queryKey,
      mutationFn: async ({ message }: { message: string }) => {
        return await sendWorkerDirectMessage(worker.id, { message });
      },
      getOptimisticMessage: ({ message }) => ({
        text: message,
        dateCreated: new Date(),
        author: {
          id: userId,
          firstName: '',
          lastName: '',
          fullName: '',
          profileImageUrl: '',
          address: {
            city: '',
            country: 'UNITED_STATES',
            googleAddress: '',
            latitude: 0,
            line1: '',
            line2: '',
            longitude: 0,
            postalCode: '',
            state: { ansiAbbreviation: '', name: '', unnabreviated: '' },
            timezoneId: '',
            timezoneName: '',
          },
          cellPhone: '',
          email: '',
        },
        chatId: (chatId || chatMeta?.id) ?? 0,
      }),
    });

  const resendMessageMutation = useMessageMutation({
    queryKey,
    mutationFn: async ({ message }: { message: string; messageId: number }) => {
      return await sendWorkerDirectMessage(worker.id, { message });
    },
    getOptimisticMessage: ({ message }) => ({
      text: message,
      dateCreated: new Date(),
      author: {
        id: userId,
        firstName: '',
        lastName: '',
        fullName: '',
        profileImageUrl: '',
        address: {
          city: '',
          country: 'UNITED_STATES',
          googleAddress: '',
          latitude: 0,
          line1: '',
          line2: '',
          longitude: 0,
          postalCode: '',
          state: { ansiAbbreviation: '', name: '', unnabreviated: '' },
          timezoneId: '',
          timezoneName: '',
        },
        cellPhone: '',
        email: '',
      },
      chatId: (chatId || chatMeta?.id) ?? 0,
    }),
  });

  const handleSubmitMessage = useCallback(
    async (message: string) => {
      if (!message) return;

      try {
        await sendMessage({ message });
      } catch (err) {
        console.error('Failed to send message:', err);
      }
    },
    [sendMessage]
  );

  const handleLoadOlder = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <GenericChat
      showWorkerInfo={!!showWorkerInfo}
      worker={worker}
      conversationId={chatId ?? chatMeta?.id ?? worker.id}
      isDirectMessage
      isAnnouncement={isAnnouncement}
      isLoading={isLoadingMessages}
      error={messagesError}
      messages={transformedMessages}
      chatMembers={chatMembers}
      onViewProfile={isAnnouncement ? undefined : viewProfile}
      resendMessageMutation={resendMessageMutation}
      onSubmitMessage={handleSubmitMessage}
      isSendingMessage={isSendingMessage}
      onClose={onClose}
      policyNotice={policyNotice}
      firstUnreadMessageId={originalDirectMessage?.firstUnreadMessageId ?? null}
      hasMoreOlder={!!hasNextPage}
      isLoadingOlder={isFetchingNextPage}
      onLoadOlder={handleLoadOlder}
    />
  );
}

export default DirectMessageChat;
